import { useMutation } from '@apollo/client';
import { get } from 'lodash';
import {
  IAccessoryList,
  IMargin,
  IMarginInput,
  INextArticle,
  IOrder,
  IRammeavtale,
  IVehicleConfiguration,
  getLatestMargin,
  mapVatPricesForNext,
} from 'next-common';
import { useEffect, useState } from 'react';
import { ArticleType, VatCode } from 'sales-types-common';
import { ICalculatePricesBasedOnArticleListResult } from 'sales-common-calculations';
import { createAndAddMarginToOrderMutationParsed } from '../../../common';
import { calculateVatForElCars } from '../../../utils';
import { CalculateDiscount } from './CalculateDiscount';
import { CalculateImporterDiscount } from './CalculateImporterDiscount';
import { FrameworkAgreement } from './FrameworkAgreement';
import { MarginCartDetails } from './MarginCartDetails';
import { checkHasMarginChanged, checkHasRammeavtaleChanged, postAndSaveMargin } from './MarginCartUtils';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectConfiguration, selectOrderId } from '@store/selectors';
import { refetchOrder } from '@store/thunks';
import { CartPrices, CartSummaryData, cn, TMessageBarSeverity } from 'types';
import { Accordion, Button, IconType, MessageBar, ToggleSwitch } from '@repo/ui/components';

interface IMarginCartProps {
  order: IOrder;
  vehicleConfiguration: IVehicleConfiguration;
  articles: INextArticle[];
  vatPrices: CartPrices;
  carType: string;
  isUnavailablePimData?: boolean;
  saveAccessories?(): Promise<IOrder>;
  accessoryChanges?: CartSummaryData[];
  onUpdateArticles?(value: IAccessoryList, customerAdvantage?: boolean, discountArticles?: INextArticle[]): void;
  isPcodePage?: boolean;
  isPcodeChangeFound?: boolean;
  isPcodePriceChangeFound?: boolean;
  isSaveFromMargin?: boolean;
  isPcodeChangeCancelled?: boolean;
  setIsSaveFromMargin?(value: boolean): void;
  setIsPcodeChangeCancelled?(vale: boolean): void;
  rammeavtale?: IRammeavtale;
  setRammeavtale?(rammeavtale: IRammeavtale): void;
}

export const MarginCart = (props: IMarginCartProps) => {
  const dispatch = useAppDispatch();
  const [checkedIndex, setCheckedIndex] = useState(false);
  const [checkedAgreement, setCheckedAgreement] = useState(false);
  const [latestMargin, setLatestMargin] = useState<IMargin>(getLatestMargin(get(props, 'order.margins', null)));
  const [activeMargin, setActiveMargin] = useState<IMarginInput>(getLatestMargin(get(props, 'order.margins', null)));
  const [updatedMargin, setUpdatedMargin] = useState<IMarginInput>(null);
  const [hasMarginChanged, setHasMarginChanged] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [vatPrices, setVatPrices] = useState<CartPrices>(null);
  const [articles, setArticles] = useState<INextArticle[]>([]);
  const [isValidCampaignDiscount, setIsValidCampaignDiscount] = useState<boolean>(true);
  const [isValidImporterDiscount, setIsValidImporterDiscount] = useState<boolean>(true);
  const [isValidAccessoriesDiscount, setIsValidAccessoriesDiscount] = useState<boolean>(true);
  const [createAndSaveMarginMutation] = useMutation(createAndAddMarginToOrderMutationParsed);
  const orderId = useAppSelector(selectOrderId);

  const configurations = useAppSelector(selectConfiguration);

  const isRammeavtaleEnabled = configurations?.features?.rammeavtale ?? false;

  useEffect(() => {
    setVatPrices(props.vatPrices);
    setArticles(props.articles);
  }, [props.vatPrices, props.articles]);

  useEffect(() => {
    setLatestMargin(getLatestMargin(get(props, 'order.margins', null)));
    setActiveMargin(getLatestMargin(get(props, 'order.margins', null)));
  }, [props.order?.margins]);

  useEffect(() => {
    setHasMarginChanged(
      checkHasMarginChanged(latestMargin, activeMargin) ||
        checkHasRammeavtaleChanged(props?.order?.rammeavtale, props?.rammeavtale)
    );
  }, [activeMargin, props?.order?.rammeavtale, props?.rammeavtale]);

  const handleSwitchToggle = (): void => {
    setCheckedIndex(!checkedIndex);
  };

  useEffect(() => {
    if (
      props.isSaveFromMargin &&
      !props.isPcodeChangeFound &&
      !props.isPcodeChangeCancelled &&
      !props.isPcodePriceChangeFound
    ) {
      async function saveMargin() {
        setLoading(true);
        await saveMarginData();
      }
      saveMargin();
    } else {
      setLoading(false);
    }
  }, [props.isSaveFromMargin, props.isPcodeChangeCancelled, props.isPcodeChangeFound]);

  const handleAgreementSwitchToggle = (): void => {
    setCheckedAgreement(!checkedAgreement);
  };

  const saveMargin = async (): Promise<void> => {
    if (hasMarginChanged) {
      setLoading(true);
      if (typeof props.saveAccessories === 'function') {
        await props?.saveAccessories();
      }
      if (props.isPcodePage) {
        props.setIsSaveFromMargin(true);
      } else {
        await saveMarginData();
      }
    }
  };

  const saveMarginData = async () => {
    const data = await postAndSaveMargin(
      get(props, 'order.id', ''),
      updatedMargin || activeMargin,
      createAndSaveMarginMutation,
      props?.rammeavtale ?? null
    );

    if (data) {
      setHasMarginChanged(false);
    }
    await dispatch(refetchOrder(orderId));
    setLoading(false);

    if (typeof props.setIsSaveFromMargin === 'function') {
      props.setIsSaveFromMargin(false);
    }
    if (typeof props.setIsPcodeChangeCancelled === 'function') {
      props.setIsPcodeChangeCancelled(null);
    }
  };

  const handleReset = (): void => {
    const updateArticleFields = (article: INextArticle, props: IMarginCartProps) => {
      switch (article?.articleType) {
        case ArticleType?.CUSTOMERADVANTAGE:
        case ArticleType?.DISCOUNT_AMOUNT:
          return {
            ...article,
            priceExclVat: props?.order?.pimSnapshot?.customerAdvantageExcludingVat || 0,
            dealerPriceExclVat: props?.order?.pimSnapshot?.customerAdvantageExcludingVat || 0,
          };
        case ArticleType?.IMPORTCONTRIBUTION:
          return {
            ...article,
            priceExclVat: props?.order?.pimSnapshot?.importerSupportExcludingVat || 0,
            dealerPriceExclVat: props?.order?.pimSnapshot?.importerSupportExcludingVat || 0,
          };
        case ArticleType?.IMPORTCONTRIBUTION_PERCENTAGE:
        case ArticleType?.DISCOUNT_PERCENTAGE:
          return {
            ...article,
            priceExclVat: 0,
            dealerPriceExclVat: 0,
          };
        default:
          return article;
      }
    };
    const updatedArticles = articles?.map((article) => updateArticleFields(article, props));

    const appendIfMissing = (
      input: INextArticle[],
      hasRequiredArticle: (list: INextArticle[]) => boolean,
      articleToAppend: INextArticle
    ): INextArticle[] => {
      return hasRequiredArticle(input) ? input : ([...(input || []), articleToAppend] as any);
    };

    const importerSupportExcludingVat = props?.order?.pimSnapshot?.importerSupportExcludingVat || 0;
    const customerAdvantageExcludingVat = props?.order?.pimSnapshot?.customerAdvantageExcludingVat || 0;

    const normalizedArticles = appendIfMissing(
      updatedArticles,
      (list) => list?.some((article) => article?.articleType === ArticleType.IMPORTCONTRIBUTION),
      {
        priceExclVat: importerSupportExcludingVat,
        vatCode: VatCode.NOT_APPLICABLE.toString(),
        articleType: ArticleType.IMPORTCONTRIBUTION,
        dealerPriceExclVat: importerSupportExcludingVat,
        discountAmountExclVat: 0,
        discountPercentage: 0,
      } as any
    );

    const fullyNormalizedArticles = appendIfMissing(
      normalizedArticles,
      (list) =>
        list?.some(
          (article) =>
            article?.articleType === ArticleType.CUSTOMERADVANTAGE ||
            article?.articleType === ArticleType.DISCOUNT_AMOUNT
        ),
      {
        priceExclVat: customerAdvantageExcludingVat,
        vatCode: VatCode.NOT_APPLICABLE.toString(),
        articleType: ArticleType.CUSTOMERADVANTAGE,
        dealerPriceExclVat: customerAdvantageExcludingVat,
        discountAmountExclVat: 0,
        discountPercentage: 0,
      } as any
    );

    let vatCalculation: ICalculatePricesBasedOnArticleListResult = null;

    vatCalculation = calculateVatForElCars(fullyNormalizedArticles, props.carType);

    const resetMargin: IMarginInput = {
      campaignDiscount: Math.abs(vatCalculation?.calculatedDiscountOnVehicle || 0),
      campaignImporter: Math.abs(vatCalculation?.calculatedImportContribution || 0),
      discountValue: props?.order?.campaignSnapshot?.customerAdvantageTerms?.customerAdvantageAmount || 0,
      importerDiscountValue: 0,

      marginValue: vatCalculation?.marginAmount,
      marginPercentage: vatCalculation?.marginPercentage,
      campaignDiscountPercentage: vatCalculation?.customerAdvantagePercentage,
      campaignImporterPercentage: 0,
    };

    setActiveMargin(resetMargin);
    setUpdatedMargin(resetMargin);
    setArticles(normalizedArticles);
    setVatPrices(mapVatPricesForNext(vatCalculation));
    if (typeof props.onUpdateArticles === 'function') {
      props.onUpdateArticles(null, true, normalizedArticles);
    }

    setIsValidCampaignDiscount(true);
    setIsValidImporterDiscount(true);
    setHasMarginChanged(false);
  };

  const getCampaignMessageBarTitle = (): React.ReactNode => {
    return (
      <span>
        Denne bilen er en del av kampanjen <b>{props?.order?.campaignSnapshot?.campaignTitle}</b>.{' '}
        {props?.order?.campaignSnapshot?.campaignDescription}.
      </span>
    );
  };

  const isCampaignMessageBarVisible = () => {
    const campaignSnapshot = props?.order?.campaignSnapshot;
    return (
      isValidCampaignDiscount &&
      campaignSnapshot &&
      (campaignSnapshot?.campaignTitle || campaignSnapshot?.campaignDescription)
    );
  };

  return (
    <div className="text-black border-t border-neutralGrey px-6 peer-empty:border-none">
      <Accordion
        className="w-full bg-lightGrey border-0 aria-expanded:outline-none aria-expanded:px-0 aria-expanded:border-none hover:bg-lightGrey"
        triggerClassName="border-0 bg-lightGrey w-full enabled:hover:bg-lightGrey px-0"
        contentClassName="border-0 bg-lightGrey w-full px-0"
        customLabel={<div className="text-base font-extrabold leading-[150%]">Rabatt</div>}
      >
        <div className="w-full flex flex-col gap-8 bg-lightGrey">
          <CalculateDiscount
            activeMargin={activeMargin}
            latestMargin={latestMargin}
            pimSnapshot={get(props, 'order.pimSnapshot', null)}
            setActiveMargin={(value) => {
              setActiveMargin(value);
              setUpdatedMargin(value);
            }}
            vehicleConfiguration={props.vehicleConfiguration}
            articles={articles}
            vatPrices={vatPrices}
            setVatPrices={setVatPrices}
            isValid={isValidCampaignDiscount}
            setIsValid={setIsValidCampaignDiscount}
            carType={props?.carType}
            order={props.order}
            onUpdateArticles={props.onUpdateArticles}
          />
          {isCampaignMessageBarVisible() && (
            <MessageBar alertIcon={IconType.NonMercedesInfoSolid} severity={TMessageBarSeverity.INFORMATION}>
              <span className="text-base/[175%] font-semibold">{getCampaignMessageBarTitle()}</span>
            </MessageBar>
          )}
          {isRammeavtaleEnabled && (
            <div className={cn('flex flex-col w-full', checkedAgreement ? 'gap-6' : 'gap-0')}>
              <ToggleSwitch
                label={'Rammeavtale'}
                checked={checkedAgreement}
                toggleHandler={handleAgreementSwitchToggle}
              />
              <div className={cn('flex flex-col w-full items-end', checkedAgreement ? 'block' : 'hidden')}>
                <FrameworkAgreement
                  pimSnapshot={get(props, 'order.pimSnapshot', null)}
                  activeMargin={activeMargin}
                  latestMargin={latestMargin}
                  setActiveMargin={(value) => {
                    setActiveMargin(value);
                    setUpdatedMargin(value);
                  }}
                  setVatPrices={setVatPrices}
                  isValid={isValidAccessoriesDiscount}
                  setIsValid={setIsValidAccessoriesDiscount}
                  order={props?.order}
                  accessoryChanges={props.accessoryChanges}
                  articles={props.articles}
                  onUpdateArticles={props.onUpdateArticles}
                  carType={props?.carType}
                  setRammeavtale={props?.setRammeavtale}
                  rammeavtale={props?.rammeavtale}
                  vehicleConfiguration={props.vehicleConfiguration}
                  checkedAgreement={checkedAgreement}
                />
              </div>
            </div>
          )}
          {!get(props, 'order.isAgent', null) && (
            <div className="flex flex-col w-full gap-4">
              <ToggleSwitch label={'Marginbilde'} checked={checkedIndex} toggleHandler={handleSwitchToggle} />
              {checkedIndex && (
                <div className="flex flex-col w-full gap-4">
                  <CalculateImporterDiscount
                    activeMargin={activeMargin}
                    latestMargin={latestMargin}
                    pimSnapshot={get(props, 'order.pimSnapshot', null)}
                    articles={articles}
                    setActiveMargin={(value) => {
                      setActiveMargin(value);
                      setUpdatedMargin(value);
                    }}
                    vehicleConfiguration={props.vehicleConfiguration}
                    isValid={isValidImporterDiscount}
                    setIsValid={setIsValidImporterDiscount}
                    carType={props?.carType}
                    order={props.order}
                    setVatPrices={setVatPrices}
                    onUpdateArticles={props.onUpdateArticles}
                  />
                  <MarginCartDetails margin={latestMargin} />
                </div>
              )}
            </div>
          )}
          <div className="flex gap-4">
            <Button
              isLoading={loading}
              variant={'purple'}
              onClick={saveMargin}
              disabled={
                !hasMarginChanged ||
                (!isValidCampaignDiscount && isValidCampaignDiscount !== null) ||
                (!isValidImporterDiscount && isValidImporterDiscount !== null) ||
                (!isValidAccessoriesDiscount && isValidAccessoriesDiscount !== null) ||
                props.isUnavailablePimData
              }
            >
              Oppdater rabatt
            </Button>
            <Button variant={'purple'} appearance="outline" onClick={handleReset} disabled={props.isUnavailablePimData}>
              Nullstill
            </Button>
          </div>
        </div>
      </Accordion>
    </div>
  );
};
